// ModelBackend transport contract (RF-BACKEND-01..05, RF-EXEC-08) and quality verdicts
// (RF-GL-06, RF-MAESTRO-04). Provider specifics live only in adapters.
import { z } from "zod";
import { AGENT_ROLES, PROVIDER_ERROR_CLASSES, SEVERITIES, VERDICTS } from "./enums.ts";

export const MODEL_CONTRACT_VERSION = 1 as const;

// ---------- Messages (RF-BACKEND-02) ----------

export const MESSAGE_ROLES = ["system", "developer", "user", "assistant", "tool_result"] as const;
export type MessageRole = (typeof MESSAGE_ROLES)[number];

export const toolCallSchema = z.object({
  call_id: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  /** Raw JSON arguments exactly as produced by the model (validated by the broker). */
  arguments: z.string().max(1_000_000),
});
export type ToolCall = z.infer<typeof toolCallSchema>;

// ---------- Image content (decision D36) ----------
//
// A message carries its text in `content` (always a string: every consumer — envelope, redaction, logs, memory,
// checkpoints — keeps working on text) and, optionally, image parts in `images`, sent AFTER the text of the same
// message. Images are only valid on `user` messages (the only role every vision protocol accepts them on). One text
// source of truth means the untrusted envelope, redaction and the taint rules can never desync from a parallel text
// part; an image of an untrusted message is data exactly like its text.

/** The formats every supported vision protocol accepts (chat completions, Responses, the `ia` module). */
export const MODEL_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;
export type ModelImageMime = (typeof MODEL_IMAGE_MIME_TYPES)[number];
/** Per image (decoded bytes). */
export const MODEL_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/** All images of one request together (decoded bytes). */
export const MODEL_IMAGES_MAX_TOTAL_BYTES = 15 * 1024 * 1024;
export const MODEL_IMAGES_PER_MESSAGE_MAX = 8;
/**
 * Worst-case input tokens of one image, for budgets and the context window (never for billing, which uses the
 * provider's usage). Default-detail costs per provider family at the time of writing: OpenAI tiles ≤ 1,445, OpenAI
 * patch models ≤ 1,536 patches × 2.46 ≈ 3,779, Anthropic ≈ w×h/750 after its own downscale ≤ ~1,600; Gemini grows with
 * the size (258 per 768×768 tile). So: max(4,096, Gemini tiles) when the dimensions are known, else 16,384.
 */
export const MODEL_IMAGE_TOKENS_FLOOR = 4096;
export const MODEL_IMAGE_TOKENS_UNKNOWN_SIZE = 16_384;

/** Known model families or IDs that natively accept image inputs (vision/multimodal). */
export function isVisionModel(externalModelId: string): boolean {
  if (!externalModelId) return false;
  const m = externalModelId.toLowerCase();
  return (
    m.includes("gemini") ||
    m.includes("gpt-4") ||
    m.includes("claude-3") ||
    m.includes("claude-sonnet") ||
    m.includes("claude-opus") ||
    m.includes("vision") ||
    m.includes("-vl") ||
    m.includes("omni") ||
    m.includes("pixtral") ||
    m.includes("qwen") ||
    m.includes("llama-3.2") ||
    m.includes("google") ||
    m.includes("anthropic")
  );
}

const base64LengthOf = (bytes: number): number => Math.ceil(bytes / 3) * 4;

export const modelImagePartSchema = z.object({
  mime: z.enum(MODEL_IMAGE_MIME_TYPES),
  /** Standard base64 of the whole image (no data: prefix). No regex: validating MBs with one would stall the process. */
  data_base64: z.string().min(4).max(base64LengthOf(MODEL_IMAGE_MAX_BYTES)),
  /** Pixel size when known (read from the image header); only used for the token estimate. */
  width: z.number().int().positive().max(100_000).optional(),
  height: z.number().int().positive().max(100_000).optional(),
});
export type ModelImagePart = z.infer<typeof modelImagePartSchema>;

export const modelMessageSchema = z.object({
  role: z.enum(MESSAGE_ROLES),
  content: z.string(),
  /** D36: image parts sent after `content` (user messages only). Absent = a text-only message, as before. */
  images: z.array(modelImagePartSchema).max(MODEL_IMAGES_PER_MESSAGE_MAX).optional(),
  /** Present on assistant messages that requested tools. */
  tool_calls: z.array(toolCallSchema).optional(),
  /** Present on tool_result messages. */
  tool_call_id: z.string().optional(),
  /** D41-C12 (E6.4): the provider's finish reason of an assistant reply; `length` = cut at the output limit. */
  finish_reason: z.enum(["stop", "tool_calls", "length", "content_filter", "other"]).optional(),
  /** Marks content that is data, never instructions (PRD, repo, logs, pages, tool output). */
  untrusted: z.boolean().default(false),
  source_ref: z.string().optional(),
});
export type ModelMessage = z.infer<typeof modelMessageSchema>;

/** Decoded size of a base64 payload, without decoding it. */
export function imageByteLength(part: Pick<ModelImagePart, "data_base64">): number {
  const s = part.data_base64;
  const padding = s.endsWith("==") ? 2 : s.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((s.length * 3) / 4) - padding);
}

/** Worst-case input tokens of one image (see MODEL_IMAGE_TOKENS_FLOOR). */
export function estimateImageTokens(part: Pick<ModelImagePart, "width" | "height">): number {
  if (part.width === undefined || part.height === undefined) return MODEL_IMAGE_TOKENS_UNKNOWN_SIZE;
  return Math.max(MODEL_IMAGE_TOKENS_FLOOR, Math.ceil(part.width / 768) * Math.ceil(part.height / 768) * 258);
}

/** The message without its image bytes (text, roles and ids unchanged): for size estimates, storage and logs. */
export function withoutImageData(message: ModelMessage): ModelMessage {
  if (message.images === undefined) return message;
  const { images: _images, ...rest } = message;
  return rest;
}

/**
 * Identified estimate of the input tokens of a request: the JSON of the messages and tools WITHOUT image bytes at
 * `charsPerToken`, plus the worst case of every image (estimateImageTokens). Base64 is never counted as text: a 5 MB
 * image would otherwise read as ~1.7 M tokens and refuse every call.
 */
export function estimateModelInputTokens(input: { messages: readonly ModelMessage[]; tools?: readonly unknown[] }, charsPerToken: number): number {
  const text = JSON.stringify({ messages: input.messages.map(withoutImageData), tools: input.tools ?? [] }).length;
  const images = input.messages.reduce((sum, m) => sum + (m.images ?? []).reduce((s, p) => s + estimateImageTokens(p), 0), 0);
  return Math.ceil(text / Math.max(0.25, charsPerToken)) + images;
}

/**
 * The message as stored or logged: image bytes are never persisted with a conversation nor written to a log; a short
 * note in the text says how many images were sent (format and size only).
 */
export function imagesAsNote(message: ModelMessage): ModelMessage {
  if (message.images === undefined || message.images.length === 0) return withoutImageData(message);
  const list = message.images.map((p) => `${p.mime} ${imageByteLength(p)} B`).join(", ");
  return { ...withoutImageData(message), content: `${message.content}\n[${message.images.length} image(s) sent with this message, not stored: ${list}]` };
}

export const toolDefinitionSchema = z.object({
  name: z.string().regex(/^[a-z][a-z0-9_.-]{0,63}$/),
  description: z.string().max(4000),
  input_schema: z.record(z.string(), z.unknown()),
});
export type ToolDefinition = z.infer<typeof toolDefinitionSchema>;

// ---------- Route pinned for the whole attempt (RF-BACKEND-04, RF-CFG-06) ----------

/** `ia_module` (D20): calls go through the provider engines of the vendored `@primage/integration-ia` module. */
export const PROTOCOLS = ["openai_responses", "openai_chat_completions", "jev_decision", "ia_module"] as const;
export type Protocol = (typeof PROTOCOLS)[number];

export const routeSnapshotSchema = z.object({
  route_version: z.number().int().nonnegative(),
  epoch: z.number().int().nonnegative(),
  connection_id: z.uuid(),
  adapter: z.string(),
  base_url: z.url(),
  protocol: z.enum(PROTOCOLS),
  model_target_id: z.uuid(),
  external_model_id: z.string(),
  quota_pool_id: z.uuid().nullable(),
  is_free: z.boolean().nullable(),
  /**
   * Only equivalences pre-authorized by policy may be accepted when the gateway returns another
   * model. Exact model ids, except on a `router_alias` route where entries are glob patterns
   * (`*` = any sequence of characters, e.g. `*:free`).
   */
  accepted_equivalent_models: z.array(z.string()).default([]),
  /**
   * Admin-set effective-model policy of the target (migration 0008). Absent = "exact" (AC-88
   * unchanged). "router_alias": the target is a router combo that picks the real model per call.
   */
  effective_model_policy: z.enum(["exact", "router_alias"]).optional(),
  /**
   * D20: the integration module instance behind an `integration_module` connection (protocol `ia_module`).
   * `card_id` selects the provider engine of `@primage/integration-ia` (openai, openrouter, anthropic, gemini,
   * nvidia-nim, compativel); the key lives in the instance's vault secret, never in the snapshot.
   */
  module: z.object({ instance_id: z.uuid(), card_id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/) }).optional(),
  /**
   * Routing Intelligence v2 (D24, D-RI-05): the attempt's routing session id, stable for the whole attempt and sent as
   * the OpenRouter `session_id` (sticky provider routing for prompt cache). Hashes and ids only, never secrets.
   */
  routing_session_id: z.string().min(1).max(256).regex(/^[A-Za-z0-9:._-]+$/).optional(),
});
export type RouteSnapshot = z.infer<typeof routeSnapshotSchema>;

export const modelRequestSchema = z.object({
  contract_version: z.literal(MODEL_CONTRACT_VERSION),
  role: z.enum(AGENT_ROLES),
  messages: z.array(modelMessageSchema).min(1),
  tools: z.array(toolDefinitionSchema).default([]),
  output: z.discriminatedUnion("type", [
    z.object({ type: z.literal("text") }),
    z.object({ type: z.literal("json_schema"), name: z.string(), schema: z.record(z.string(), z.unknown()) }),
  ]),
  max_output_tokens: z.number().int().positive(),
  reasoning_effort: z.enum(["low", "medium", "high", "xhigh", "max", "ultra"]).optional(),
  metadata: z.object({
    organization_id: z.uuid(),
    project_id: z.uuid().nullable(),
    attempt_id: z.uuid().nullable(),
    model_call_id: z.uuid(),
    sequence: z.number().int().nonnegative(),
  }),
});
export type ModelRequest = z.infer<typeof modelRequestSchema>;

// ---------- Streamed output events ----------

export const usageSchema = z.object({
  input_tokens: z.number().int().nonnegative().nullable(),
  output_tokens: z.number().int().nonnegative().nullable(),
  cache_read_tokens: z.number().int().nonnegative().nullable(),
  cache_write_tokens: z.number().int().nonnegative().nullable(),
  reasoning_tokens: z.number().int().nonnegative().nullable(),
  method: z.enum(["provider", "tokenizer", "estimate", "unknown"]),
  cost_billed_cents: z.number().nonnegative().nullable(),
});
export type Usage = z.infer<typeof usageSchema>;

export type ModelEvent =
  | { type: "text_delta"; text: string }
  | { type: "reasoning_summary"; text: string }
  | { type: "tool_call"; call: ToolCall }
  | { type: "usage"; usage: Usage }
  | {
      type: "error";
      errorClass: (typeof PROVIDER_ERROR_CLASSES)[number];
      message: string;
      retryAfterMs: number | null;
      /** Whether any part of the request may have been accepted by the provider. */
      acceptance: "none" | "partial" | "unknown";
    }
  | { type: "completed"; finishReason: "stop" | "tool_calls" | "length" | "content_filter" | "other"; effectiveModel: string | null; provider: string | null };

/**
 * Whether the backend keeps conversation state between calls (RF-CHAT-06, AC-41). A stateless
 * backend must always receive the full history; a delta-only continuation is never sent to it.
 * Absent means "stateless" (the safe default).
 */
export const SESSION_AFFINITIES = ["stateless", "stateful"] as const;
export type SessionAffinity = (typeof SESSION_AFFINITIES)[number];

/** Adapter contract: one instance per protocol; the route never changes within a call. */
export interface ModelBackend {
  readonly protocol: Protocol;
  readonly sessionAffinity?: SessionAffinity;
  stream(request: ModelRequest, route: RouteSnapshot, credential: string | null, signal: AbortSignal): AsyncIterable<ModelEvent>;
}

// ---------- Verdicts (RF-GL-06, RF-MAESTRO-04) ----------

export const findingSchema = z.object({
  criterion_id: z.string().min(1),
  severity: z.enum(SEVERITIES),
  location: z.string().nullable(),
  observed_failure: z.string().min(1),
  resolution_condition: z.string().min(1),
  evidence_refs: z.array(z.string()).default([]),
});
export type Finding = z.infer<typeof findingSchema>;

export const criticVerdictSchema = z.object({
  candidate_hash: z.string().min(7),
  criterion_id: z.string().min(1),
  verdict: z.enum(VERDICTS),
  findings: z.array(findingSchema),
  severity: z.enum(SEVERITIES),
  evidence_refs: z.array(z.string()),
  next_action: z.string(),
});
export type CriticVerdict = z.infer<typeof criticVerdictSchema>;

export const maestroVerdictSchema = z.object({
  candidate_hash: z.string().min(7),
  requirement_ids: z.array(z.string()).min(1),
  verdict: z.enum(VERDICTS),
  findings: z.array(findingSchema),
  severity: z.enum(SEVERITIES),
  evidence_refs: z.array(z.string()),
  required_actions: z.array(z.string()),
  review_model: z.string().min(1),
});
export type MaestroVerdict = z.infer<typeof maestroVerdictSchema>;

// ---------- Evaluation contract (RF-GL-02) ----------

export const evaluationContractSchema = z.object({
  objective: z.string().min(1),
  requirement_ids: z.array(z.string()).min(1),
  mandatory_criteria: z.array(z.object({ id: z.string(), text: z.string(), verification: z.string() })).min(1),
  optional_criteria: z.array(z.object({ id: z.string(), text: z.string() })).default([]),
  reference: z.object({ kind: z.enum(["contract", "fixture", "visual", "flow", "test_suite", "document"]), ref: z.string(), hash: z.string() }),
  environment: z.string(),
  verification_tools: z.array(z.string()),
  delivery_target: z.string(),
});
export type EvaluationContract = z.infer<typeof evaluationContractSchema>;

// ---------- Context packet and checkpoint (RF-CHAT-04, RF-CTX-01) ----------

export const contextPacketSchema = z.object({
  schema_version: z.literal(1),
  literal_request: z.string().min(1),
  objective: z.string(),
  requirement_ids: z.array(z.string()),
  acceptance_criteria: z.array(z.string()),
  constraints: z.array(z.string()),
  sources: z.array(z.object({ ref: z.string(), hash: z.string() })),
  excerpts: z.array(z.object({ ref: z.string(), text: z.string() })),
  proven_decisions: z.array(z.string()),
  allowed_tools: z.array(z.string()),
  pending_actions: z.array(z.string()),
  /** Set when the request had to be partitioned explicitly (never silently truncated). */
  partition: z.object({ index: z.number().int(), total: z.number().int() }).nullable(),
});
export type ContextPacket = z.infer<typeof contextPacketSchema>;

const checkpointBodyV1Fields = {
  session_id: z.string(),
  epoch: z.number().int().nonnegative(),
  source_hash: z.string(),
  task_version: z.number().int(),
  last_literal_request: z.string().min(1),
  mandatory_requirement_ids: z.array(z.string()),
  confirmed_decisions: z.array(z.string()),
  hypotheses: z.array(z.string()),
  files: z.array(z.string()),
  commits: z.array(z.string()),
  tests: z.array(z.object({ ref: z.string(), result: z.string() })),
  pending_external_operations: z.array(z.object({ idempotency_key: z.string(), kind: z.string(), state: z.string() })),
  next_action: z.string().min(1),
};

/** Opaque reference to evidence preserved across a compaction (ids only, never inline content). */
export const checkpointEvidenceRefSchema = z.object({
  kind: z.enum(["evidence", "artifact", "gate_result", "browser_run", "model_call"]),
  id: z.string().min(1).max(200),
});
export type CheckpointEvidenceRef = z.infer<typeof checkpointEvidenceRefSchema>;

export const checkpointBodyV1Schema = z.object({ schema_version: z.literal(1), ...checkpointBodyV1Fields });
/** v2 (council D11 item 6): adds evidence references preserved by the compaction (AC-25). */
export const checkpointBodyV2Schema = z.object({
  schema_version: z.literal(2),
  ...checkpointBodyV1Fields,
  evidence_refs: z.array(checkpointEvidenceRefSchema).max(500),
});
export const checkpointAcceptanceCriterionSchema = z.object({ id: z.string().min(1).max(200), text: z.string().max(4000) });
export const checkpointRelevantErrorSchema = z.object({ ref: z.string().min(1).max(200), summary: z.string().max(2000) });
/** v3 (AC32): the compaction also carries the task's acceptance criteria and its relevant errors. */
export const checkpointBodyV3Schema = z.object({
  schema_version: z.literal(3),
  ...checkpointBodyV1Fields,
  evidence_refs: z.array(checkpointEvidenceRefSchema).max(500),
  acceptance_criteria: z.array(checkpointAcceptanceCriterionSchema).max(200),
  relevant_errors: z.array(checkpointRelevantErrorSchema).max(200),
});
/** Every stored checkpoint version stays valid: new versions are added, never mutated. */
export const checkpointBodySchema = z.discriminatedUnion("schema_version", [checkpointBodyV1Schema, checkpointBodyV2Schema, checkpointBodyV3Schema]);
export type CheckpointBody = z.infer<typeof checkpointBodySchema>;
